import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/** Caixa de seleção com estado "parcial" (para o "selecionar todos" quando só alguns estão marcados). */
export function Checkbox({ checked, indeterminate = false, onChange, label, disabled }: { checked: boolean; indeterminate?: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate && !checked; }, [indeterminate, checked]);
  return (
    <label className="k-check" onClick={(e) => e.stopPropagation()}>
      <input ref={ref} type="checkbox" checked={checked} disabled={disabled} aria-label={label} onChange={(e) => onChange(e.target.checked)} />
      <span className="k-check__box" aria-hidden="true" />
    </label>
  );
}

/**
 * Seleção múltipla de uma lista: marca/desmarca, "todos" e limpa. Itens que somem da lista (ex.: excluídos)
 * saem da seleção sozinhos.
 */
export function useSelection<T>(items: readonly T[], getId: (item: T) => string) {
  const [ids, setIds] = useState<Set<string>>(() => new Set());
  const all = useMemo(() => items.map(getId), [items, getId]);
  useEffect(() => { setIds((prev) => { const next = new Set([...prev].filter((id) => all.includes(id))); return next.size === prev.size ? prev : next; }); }, [all]);
  const toggle = useCallback((id: string, on?: boolean) => setIds((prev) => { const next = new Set(prev); if (on ?? !next.has(id)) next.add(id); else next.delete(id); return next; }), []);
  const toggleAll = useCallback((on: boolean) => setIds(on ? new Set(all) : new Set()), [all]);
  const clear = useCallback(() => setIds(new Set()), []);
  const selected = useMemo(() => items.filter((i) => ids.has(getId(i))), [items, ids, getId]);
  return { ids, selected, count: ids.size, isSelected: (id: string) => ids.has(id), toggle, toggleAll, clear, allChecked: all.length > 0 && ids.size === all.length, someChecked: ids.size > 0 && ids.size < all.length };
}

/** Barra de ações em lote: aparece fixa no rodapé enquanto houver itens selecionados. */
export function BulkBar({ count, label, clearLabel, onClear, children }: { count: number; label: string; clearLabel: string; onClear: () => void; children: ReactNode }) {
  if (!count) return null;
  return (
    <div className="k-bulkbar" role="region" aria-label={label}>
      <strong className="k-bulkbar__count">{label}</strong>
      <div className="k-bulkbar__actions">{children}</div>
      <button type="button" className="k-bulkbar__clear" onClick={onClear}>{clearLabel}</button>
    </div>
  );
}
